import React, { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  User, UserPlus, LogIn, LogOut, Search, Plus, X, Check, 
  Camera, Printer, Clock, Building, Phone, Mail, Car,
  Filter, Download, Calendar, Shield, AlertTriangle, Eye
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VisitorPassManagement = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [purposeFilter, setPurposeFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showCheckInDialog, setShowCheckInDialog] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  // Form state for new visitor
  const [newVisitor, setNewVisitor] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    purpose: 'meeting',
    host_name: '',
    host_department: '',
    vehicle_plate: '',
    id_type: 'national_id',
    id_number: '',
    notes: '',
    pass_type: 'single_day',
    valid_from: new Date().toISOString().split('T')[0],
    valid_until: new Date().toISOString().split('T')[0],
    escort_required: false
  });

  // Get the appropriate token based on context
  const token = getToken() || localStorage.getItem('security_token') || localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch visitors
  const { data: visitors = [], isLoading } = useQuery({
    queryKey: ['visitors', eventId, statusFilter, purposeFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (eventId) params.append('event_id', eventId);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (purposeFilter !== 'all') params.append('purpose', purposeFilter);
      const res = await fetch(`${API_URL}/api/visitors?${params}`, { headers });
      if (!res.ok) throw new Error('Failed to fetch visitors');
      return res.json();
    }
  });

  // Fetch stats
  const { data: stats = {} } = useQuery({
    queryKey: ['visitor-stats', eventId],
    queryFn: async () => {
      const params = eventId ? `?event_id=${eventId}` : '';
      const res = await fetch(`${API_URL}/api/visitors/stats${params}`, { headers });
      if (!res.ok) throw new Error('Failed to fetch stats');
      return res.json();
    }
  });

  // Create visitor mutation
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/visitors`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...data, event_id: eventId })
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create visitor');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['visitors']);
      queryClient.invalidateQueries(['visitor-stats']);
      setShowAddDialog(false);
      setCapturedPhoto(null);
      setNewVisitor({
        name: '', email: '', phone: '', company: '', purpose: 'meeting',
        host_name: '', host_department: '', vehicle_plate: '', id_type: 'national_id',
        id_number: '', notes: '', pass_type: 'single_day',
        valid_from: new Date().toISOString().split('T')[0],
        valid_until: new Date().toISOString().split('T')[0],
        escort_required: false
      });
      toast.success('Visitor pass created');
    },
    onError: (error) => toast.error(error.message)
  });

  // Check-in mutation
  const checkInMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/visitors/check-in`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Check-in failed');
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['visitors']);
      queryClient.invalidateQueries(['visitor-stats']);
      setShowCheckInDialog(false);
      setSelectedVisitor(null);
      toast.success(data.message);
    },
    onError: (error) => toast.error(error.message)
  });

  // Check-out mutation
  const checkOutMutation = useMutation({
    mutationFn: async (visitorId) => {
      const res = await fetch(`${API_URL}/api/visitors/${visitorId}/check-out`, {
        method: 'POST',
        headers
      });
      if (!res.ok) throw new Error('Check-out failed');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['visitors']);
      queryClient.invalidateQueries(['visitor-stats']);
      toast.success('Visitor checked out');
    },
    onError: (error) => toast.error(error.message)
  });

  // Print pass
  const printPass = async (visitorId) => {
    try {
      const res = await fetch(`${API_URL}/api/visitors/${visitorId}/pass-pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to generate pass');
      
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `visitor_pass_${visitorId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Pass downloaded');
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Camera functions
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: 640, height: 480 } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
    } catch (err) {
      toast.error('Could not access camera');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const capturePhoto = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedPhoto(dataUrl);
    }
  }, []);

  // Filter visitors
  const filteredVisitors = visitors.filter(v =>
    v.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.pass_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const purposeColors = {
    'meeting': 'bg-blue-100 text-blue-800',
    'delivery': 'bg-yellow-100 text-yellow-800',
    'interview': 'bg-purple-100 text-purple-800',
    'event': 'bg-green-100 text-green-800',
    'maintenance': 'bg-gray-100 text-gray-800',
    'other': 'bg-gray-100 text-gray-800'
  };

  const statusColors = {
    'active': 'bg-blue-100 text-blue-800',
    'checked_in': 'bg-green-100 text-green-800',
    'checked_out': 'bg-gray-100 text-gray-800',
    'revoked': 'bg-red-100 text-red-800'
  };

  return (
    <div className="space-y-6" data-testid="visitor-pass-management">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Today's Visitors</p>
                <p className="text-2xl font-bold">{stats.today_registered || 0}</p>
              </div>
              <User className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Checked In</p>
                <p className="text-2xl font-bold text-green-600">{stats.today_checked_in || 0}</p>
              </div>
              <LogIn className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Currently On-Site</p>
                <p className="text-2xl font-bold text-purple-600">{stats.currently_on_site || 0}</p>
              </div>
              <Building className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">By Purpose</p>
                <div className="flex gap-1 mt-1">
                  {Object.entries(stats.by_purpose || {}).slice(0, 3).map(([purpose, count]) => (
                    <Badge key={purpose} variant="secondary" className="text-xs">
                      {purpose}: {count}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search name, company, pass code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-64"
              data-testid="visitor-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="checked_in">Checked In</SelectItem>
              <SelectItem value="checked_out">Checked Out</SelectItem>
              <SelectItem value="revoked">Revoked</SelectItem>
            </SelectContent>
          </Select>
          <Select value={purposeFilter} onValueChange={setPurposeFilter}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Purpose" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Purposes</SelectItem>
              <SelectItem value="meeting">Meeting</SelectItem>
              <SelectItem value="delivery">Delivery</SelectItem>
              <SelectItem value="interview">Interview</SelectItem>
              <SelectItem value="event">Event</SelectItem>
              <SelectItem value="maintenance">Maintenance</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCheckInDialog(true)} data-testid="quick-checkin-btn">
            <LogIn className="h-4 w-4 mr-2" />
            Quick Check-in
          </Button>
          <Button onClick={() => setShowAddDialog(true)} data-testid="create-pass-btn">
            <UserPlus className="h-4 w-4 mr-2" />
            Create Pass
          </Button>
        </div>
      </div>

      {/* Visitors Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading visitors...</div>
          ) : filteredVisitors.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <User className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No visitors found</p>
              <p className="text-sm">Create a visitor pass to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Visitor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pass Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Purpose</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Host</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valid Until</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredVisitors.map((visitor) => (
                    <tr key={visitor.id} className="hover:bg-gray-50" data-testid={`visitor-row-${visitor.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {visitor.primary_photo ? (
                            <img 
                              src={`${API_URL}${visitor.primary_photo}`} 
                              alt={visitor.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <User className="h-5 w-5 text-gray-400" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium">{visitor.name}</p>
                            <p className="text-sm text-gray-500">{visitor.company || '-'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                          {visitor.pass_code}
                        </code>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={purposeColors[visitor.purpose] || 'bg-gray-100'}>
                          {visitor.purpose}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {visitor.host_name || '-'}
                        {visitor.host_department && (
                          <span className="block text-xs text-gray-400">{visitor.host_department}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Badge className={statusColors[visitor.status] || 'bg-gray-100'}>
                            {visitor.status?.replace('_', ' ')}
                          </Badge>
                          {visitor.escort_required && (
                            <Shield className="h-4 w-4 text-orange-500" title="Escort Required" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {visitor.valid_until ? new Date(visitor.valid_until).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-2 justify-end">
                          {visitor.status === 'active' && (
                            <Button 
                              size="sm"
                              onClick={() => {
                                setSelectedVisitor(visitor);
                                setShowCheckInDialog(true);
                              }}
                              data-testid={`checkin-btn-${visitor.id}`}
                            >
                              <LogIn className="h-4 w-4 mr-1" />
                              Check In
                            </Button>
                          )}
                          {visitor.status === 'checked_in' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => checkOutMutation.mutate(visitor.id)}
                              data-testid={`checkout-btn-${visitor.id}`}
                            >
                              <LogOut className="h-4 w-4 mr-1" />
                              Check Out
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => printPass(visitor.id)}
                            data-testid={`print-btn-${visitor.id}`}
                          >
                            <Printer className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Pass Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => {
        setShowAddDialog(open);
        if (!open) {
          stopCamera();
          setCapturedPhoto(null);
          setNewVisitor({
            name: '', email: '', phone: '', company: '', purpose: 'meeting',
            host_name: '', host_department: '', vehicle_plate: '', id_type: 'national_id',
            id_number: '', notes: '', pass_type: 'single_day',
            valid_from: new Date().toISOString().split('T')[0],
            valid_until: new Date().toISOString().split('T')[0],
            escort_required: false
          });
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Visitor Pass</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!newVisitor.name.trim()) {
                toast.error('Visitor name is required');
                return;
              }
              createMutation.mutate({
                ...newVisitor,
                photo_base64: capturedPhoto
              });
            }}
            className="space-y-4"
          >
            <Tabs defaultValue="info">
              <TabsList className="grid grid-cols-2 sm:grid-cols-3">
                <TabsTrigger value="info">Visitor Info</TabsTrigger>
                <TabsTrigger value="pass">Pass Settings</TabsTrigger>
                <TabsTrigger value="photo" onClick={startCamera}>Photo</TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Full Name *</label>
                    <Input 
                      value={newVisitor.name}
                      onChange={(e) => setNewVisitor(prev => ({ ...prev, name: e.target.value }))}
                      required 
                      placeholder="John Doe" 
                      data-testid="input-visitor-name" 
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Company</label>
                    <Input 
                      value={newVisitor.company}
                      onChange={(e) => setNewVisitor(prev => ({ ...prev, company: e.target.value }))}
                      placeholder="Company name" 
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Email</label>
                    <Input 
                      value={newVisitor.email}
                      onChange={(e) => setNewVisitor(prev => ({ ...prev, email: e.target.value }))}
                      type="email" 
                      placeholder="email@example.com" 
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Phone</label>
                    <Input 
                      value={newVisitor.phone}
                      onChange={(e) => setNewVisitor(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+1 234 567 8900" 
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Purpose *</label>
                    <Select 
                      value={newVisitor.purpose} 
                      onValueChange={(value) => setNewVisitor(prev => ({ ...prev, purpose: value }))}
                    >
                      <SelectTrigger data-testid="select-purpose">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="meeting">Meeting</SelectItem>
                        <SelectItem value="delivery">Delivery</SelectItem>
                        <SelectItem value="interview">Interview</SelectItem>
                        <SelectItem value="event">Event</SelectItem>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Vehicle Plate (if any)</label>
                    <Input 
                      value={newVisitor.vehicle_plate}
                      onChange={(e) => setNewVisitor(prev => ({ ...prev, vehicle_plate: e.target.value }))}
                      placeholder="ABC 123" 
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Host Name</label>
                    <Input 
                      value={newVisitor.host_name}
                      onChange={(e) => setNewVisitor(prev => ({ ...prev, host_name: e.target.value }))}
                      placeholder="Person being visited" 
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Host Department</label>
                    <Input 
                      value={newVisitor.host_department}
                      onChange={(e) => setNewVisitor(prev => ({ ...prev, host_department: e.target.value }))}
                      placeholder="Department" 
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">ID Type</label>
                    <Select 
                      value={newVisitor.id_type} 
                      onValueChange={(value) => setNewVisitor(prev => ({ ...prev, id_type: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="national_id">National ID</SelectItem>
                        <SelectItem value="drivers_license">Driver's License</SelectItem>
                        <SelectItem value="passport">Passport</SelectItem>
                        <SelectItem value="company_id">Company ID</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">ID Number</label>
                    <Input 
                      value={newVisitor.id_number}
                      onChange={(e) => setNewVisitor(prev => ({ ...prev, id_number: e.target.value }))}
                      placeholder="ID number" 
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Notes</label>
                  <Textarea 
                    value={newVisitor.notes}
                    onChange={(e) => setNewVisitor(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Additional notes..." 
                    rows={2} 
                  />
                </div>
              </TabsContent>

              <TabsContent value="pass" className="space-y-4 mt-4">
                <div>
                  <label className="text-sm font-medium">Pass Validity</label>
                  <Select 
                    value={newVisitor.pass_type} 
                    onValueChange={(value) => setNewVisitor(prev => ({ ...prev, pass_type: value }))}
                  >
                    <SelectTrigger data-testid="select-pass-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single_day">Single Day Only</SelectItem>
                      <SelectItem value="custom_range">Custom Date Range</SelectItem>
                      <SelectItem value="event_duration">Event Duration</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Valid From</label>
                    <Input 
                      value={newVisitor.valid_from}
                      onChange={(e) => setNewVisitor(prev => ({ ...prev, valid_from: e.target.value }))}
                      type="datetime-local" 
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Valid Until</label>
                    <Input 
                      value={newVisitor.valid_until}
                      onChange={(e) => setNewVisitor(prev => ({ ...prev, valid_until: e.target.value }))}
                      type="datetime-local" 
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="escort_required" 
                    checked={newVisitor.escort_required}
                    onCheckedChange={(checked) => setNewVisitor(prev => ({ ...prev, escort_required: checked }))}
                  />
                  <label htmlFor="escort_required" className="text-sm">
                    Escort required (visitor must be accompanied)
                  </label>
                </div>
              </TabsContent>

              <TabsContent value="photo" className="space-y-4 mt-4">
                {capturedPhoto ? (
                  <div className="relative">
                    <img src={capturedPhoto} alt="Captured" className="w-full max-w-sm mx-auto rounded-lg" />
                    <Button 
                      type="button"
                      size="sm"
                      variant="destructive"
                      className="absolute top-2 right-2"
                      onClick={() => {
                        setCapturedPhoto(null);
                        startCamera();
                      }}
                    >
                      <X className="h-4 w-4" /> Retake
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="relative bg-black rounded-lg overflow-hidden max-w-sm mx-auto">
                      <video ref={videoRef} autoPlay playsInline className="w-full" />
                      <canvas ref={canvasRef} className="hidden" />
                    </div>
                    <div className="flex justify-center">
                      <Button type="button" onClick={capturePhoto} data-testid="capture-photo-btn">
                        <Camera className="h-4 w-4 mr-2" />
                        Capture Photo
                      </Button>
                    </div>
                  </>
                )}
              </TabsContent>
            </Tabs>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={createMutation.isPending} data-testid="submit-pass-btn">
                {createMutation.isPending ? 'Creating...' : 'Create Pass'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick Check-in Dialog */}
      <Dialog open={showCheckInDialog} onOpenChange={(open) => {
        setShowCheckInDialog(open);
        if (!open) setSelectedVisitor(null);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Visitor Check-in</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const form = new FormData(e.target);
              checkInMutation.mutate({
                visitor_id: selectedVisitor?.id,
                pass_code: selectedVisitor ? null : form.get('pass_code'),
                gate: form.get('gate')
              });
            }}
            className="space-y-4"
          >
            {selectedVisitor ? (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium">{selectedVisitor.name}</p>
                <p className="text-sm text-gray-500">{selectedVisitor.company}</p>
                <code className="text-sm font-mono">{selectedVisitor.pass_code}</code>
              </div>
            ) : (
              <div>
                <label className="text-sm font-medium">Pass Code</label>
                <Input 
                  name="pass_code" 
                  required 
                  placeholder="VIS-XXXXXXXX"
                  className="font-mono"
                  data-testid="input-pass-code"
                />
              </div>
            )}
            <div>
              <label className="text-sm font-medium">Gate</label>
              <Select name="gate" defaultValue="main">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="main">Main Gate</SelectItem>
                  <SelectItem value="north">North Gate</SelectItem>
                  <SelectItem value="south">South Gate</SelectItem>
                  <SelectItem value="reception">Reception</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowCheckInDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={checkInMutation.isPending} data-testid="confirm-checkin-btn">
                <Check className="h-4 w-4 mr-2" />
                {checkInMutation.isPending ? 'Processing...' : 'Check In'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VisitorPassManagement;
